import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { PageHeader, useTableSort, SortableTH } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Trash2, Sparkles } from "lucide-react";
import { toast } from "sonner";

// Bank Holidays screen keeps the fuller DD MMM YYYY format (per requirement).
const fmtBH = (d) => {
  if (!d) return "—";
  try {
    return new Date(d).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
  } catch { return d; }
};

export default function BankHolidays() {
  const [years, setYears] = useState([]);
  const [yearId, setYearId] = useState("");
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ date: "", name: "", region: "england-and-wales" });

  const loadYears = () => api.get("/holiday-years").then((r) => { setYears(r.data); if (r.data[0] && !yearId) setYearId(r.data[0].id); });
  const load = () => yearId && api.get("/bank-holidays", { params: { year_id: yearId } }).then((r) => setRows(r.data));
  useEffect(() => { loadYears(); }, []);
  useEffect(() => { load(); }, [yearId]);

  const add = async () => {
    try { await api.post("/bank-holidays", { ...f, holiday_year_id: yearId, active: true }); toast.success("Added"); setOpen(false); setF({ date: "", name: "", region: "england-and-wales" }); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const seed = async () => { const { data } = await api.post(`/bank-holidays/seed/${yearId}`); toast.success(`${data.created} bank holidays added`); load(); };
  const del = async (id) => { await api.delete(`/bank-holidays/${id}`); load(); };
  const { sorted, sort, toggle } = useTableSort(rows, {
    date: (b) => b.date, name: (b) => b.name, region: (b) => b.region, status: (b) => (b.active ? 1 : 0),
  }, { key: "date", dir: "asc" });

  return (
    <div className="space-y-5">
      <PageHeader title="Bank Holidays" subtitle="Maintain the CASS bank-holiday calendar">
        <Button variant="outline" onClick={seed} data-testid="seed-bh-btn"><Sparkles size={16} className="mr-2" />Seed UK holidays</Button>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button className="bg-[#2EA3FD] hover:bg-[#1B8EE6]" data-testid="add-bh-btn"><Plus size={16} className="mr-2" />Add</Button></DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Add bank holiday</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div><Label className="text-xs text-slate-500">Date</Label><Input type="date" value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} data-testid="bh-date-input" /></div>
              <div><Label className="text-xs text-slate-500">Name</Label><Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="bh-name-input" /></div>
            </div>
            <DialogFooter><Button onClick={add} className="bg-[#2EA3FD]" data-testid="bh-save-btn">Save</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </PageHeader>

      <Select value={yearId} onValueChange={setYearId}>
        <SelectTrigger className="max-w-xs bg-white"><SelectValue placeholder="Holiday year" /></SelectTrigger>
        <SelectContent>{years.map((y) => <SelectItem key={y.id} value={y.id}>{y.name}</SelectItem>)}</SelectContent>
      </Select>

      <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-x-auto cass-scroll">
        <Table>
          <TableHeader><TableRow className="bg-slate-50">
            <SortableTH label="Date" sortId="date" sort={sort} onSort={toggle} />
            <SortableTH label="Name" sortId="name" sort={sort} onSort={toggle} />
            <SortableTH label="Region" sortId="region" sort={sort} onSort={toggle} />
            <SortableTH label="Status" sortId="status" sort={sort} onSort={toggle} />
            <TableHead className="text-xs uppercase text-slate-600"></TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {sorted.map((b) => (
              <TableRow key={b.id} data-testid={`bh-row-${b.date}`}>
                <TableCell>{fmtBH(b.date)}</TableCell>
                <TableCell className="font-medium">{b.name}</TableCell>
                <TableCell className="text-xs">{b.region}</TableCell>
                <TableCell><span className={`text-xs px-2 py-0.5 rounded-full ${b.active ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>{b.active ? "Active" : "Excluded"}</span></TableCell>
                <TableCell><Button variant="ghost" size="sm" className="text-rose-600" onClick={() => del(b.id)}><Trash2 size={14} /></Button></TableCell>
              </TableRow>
            ))}
            {rows.length === 0 && <TableRow><TableCell colSpan={5} className="text-center text-slate-400 py-8">No bank holidays — use "Seed UK holidays"</TableCell></TableRow>}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
